import React, { useState, useEffect, useMemo } from "react";
import {
  Banknote,
  TrendingDown,
  Clock,
  Bus,
  Plug,
  Sliders,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Leaf,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { api, fmtEur, fmtEurFull, fmtInt, fmtDec } from "../lib/api";
import KPICard from "../components/KPICard";

const DEFAULTS = {
  coste_vehiculo_8m: 280000,
  coste_vehiculo_10m: 350000,
  coste_vehiculo_12m: 450000,
  coste_vehiculo_15m: 580000,
  coste_vehiculo_18m: 720000,
  coste_cargador_depot: 45000,
  coste_cargador_oportunidad: 220000,
  coste_pantografo: 520000,
  coste_obra_civil_punto: 25000,
  precio_energia_eur_kwh: 0.14,
  coste_mantenimiento_electrico_eur_km: 0.18,
  coste_mantenimiento_diesel_eur_km: 0.28,
  consumo_diesel_l_km: 0.45,
  precio_diesel_eur_l: 1.45,
  vida_util_vehiculo: 12,
  vida_util_infraestructura: 20,
  tasa_descuento: 0.05,
};

const LOTE_COLORS_FIXED = { "CM-11": "#14532D", "CM-31": "#365314", "CM-32": "#7C2D12", "CM-41": "#15803D" };
const LOTE_COLOR_POOL = ["#0F766E", "#1D4ED8", "#9D174D", "#A16207", "#5B21B6", "#B45309", "#0E7490", "#4D7C0F"];
const LOTE_COLORS = new Proxy(LOTE_COLORS_FIXED, {
  get(target, prop) {
    if (prop in target) return target[prop];
    // Hash determinista por nombre del lote para que el color sea estable entre renders.
    let h = 0;
    for (let i = 0; i < String(prop).length; i++) h = ((h << 5) - h + String(prop).charCodeAt(i)) | 0;
    return LOTE_COLOR_POOL[Math.abs(h) % LOTE_COLOR_POOL.length];
  },
});

export default function Inversion() {
  const [params, setParams] = useState(DEFAULTS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedLote, setExpandedLote] = useState(null);

  const calc = async (p = params) => {
    setLoading(true);
    try {
      const r = await api.post("/inversion", p);
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    calc(DEFAULTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setP = (k, v) => setParams({ ...params, [k]: v });
  const reset = () => {
    setParams(DEFAULTS);
    calc(DEFAULTS);
  };

  const breakdownData = useMemo(() => {
    if (!data) return [];
    return [
      { name: "Vehículos", value: data.totales.capex_vehiculos, color: "#14532D" },
      { name: "Infraestructura", value: data.totales.capex_infraestructura, color: "#0F1A14" },
    ];
  }, [data]);

  const loteChart = useMemo(() => {
    if (!data) return [];
    return data.por_lote.map((l) => ({
      lote: l.lote,
      CAPEX: Math.round(l.capex_total / 1_000_000),
      "OPEX anual": Math.round(l.opex_anual_total / 1_000_000),
      "Ahorro anual": Math.round(l.ahorro_anual / 1_000_000),
    }));
  }, [data]);

  return (
    <div className="space-y-6" data-testid="inversion-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">
          Plan de inversión
        </div>
        <h1 className="section-title mt-1">CAPEX / OPEX de electrificación</h1>
        <p className="section-sub">
          Estimación de inversión (vehículos + infraestructura) y costes operativos anuales
          frente a la operación actual con vehículos diésel. Parámetros ajustables.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
        <KPICard
          testId="kpi-capex-total"
          label="CAPEX total"
          value={data ? fmtEur(data.totales.capex_total) : "—"}
          hint={data ? `${fmtInt(data.totales.n_vehiculos)} vehículos` : ""}
          icon={Banknote}
          tone="blue"
        />
        <KPICard
          testId="kpi-capex-vehiculos"
          label="CAPEX vehículos"
          value={data ? fmtEur(data.totales.capex_vehiculos) : "—"}
          hint={data ? `${((data.totales.capex_vehiculos / data.totales.capex_total) * 100).toFixed(0)}% del total` : ""}
          icon={Bus}
        />
        <KPICard
          testId="kpi-capex-infra"
          label="CAPEX infraestructura"
          value={data ? fmtEur(data.totales.capex_infraestructura) : "—"}
          hint="Cargadores + obra civil"
          icon={Plug}
        />
        <KPICard
          testId="kpi-opex-anual"
          label="OPEX anual eléctrico"
          value={data ? fmtEur(data.totales.opex_anual_total) : "—"}
          hint="Energía + mantenimiento"
          icon={TrendingDown}
          tone="red"
        />
        <KPICard
          testId="kpi-ahorro"
          label="Ahorro anual vs diésel"
          value={data ? fmtEur(data.totales.ahorro_anual) : "—"}
          hint={
            data?.totales.payback_global
              ? `Payback ${data.totales.payback_global} años`
              : "—"
          }
          icon={Leaf}
          tone="green"
        />
      </div>

      {/* Parameter panel */}
      <div className="crtm-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-[15px] flex items-center gap-2">
            <Sliders size={16} /> Parámetros del plan
          </h3>
          <div className="flex gap-2">
            <button
              className="btn-secondary text-xs"
              onClick={reset}
              data-testid="btn-reset-inversion"
            >
              <RefreshCw size={13} /> Valores por defecto
            </button>
            <button
              className="btn-primary text-xs"
              onClick={() => calc()}
              disabled={loading}
              data-testid="btn-calcular-inversion"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              {loading ? "Calculando…" : "Recalcular"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <NumField label="Bus 8 m" sufix="€" value={params.coste_vehiculo_8m} onChange={(v) => setP("coste_vehiculo_8m", v)} step={10000} />
          <NumField label="Bus 10 m" sufix="€" value={params.coste_vehiculo_10m} onChange={(v) => setP("coste_vehiculo_10m", v)} step={10000} />
          <NumField label="Bus 12 m" sufix="€" value={params.coste_vehiculo_12m} onChange={(v) => setP("coste_vehiculo_12m", v)} step={10000} />
          <NumField label="Bus 15 m" sufix="€" value={params.coste_vehiculo_15m} onChange={(v) => setP("coste_vehiculo_15m", v)} step={10000} />
          <NumField label="Bus 18 m articulado" sufix="€" value={params.coste_vehiculo_18m} onChange={(v) => setP("coste_vehiculo_18m", v)} step={10000} />

          <NumField label="Precio energía" sufix="€/kWh" value={params.precio_energia_eur_kwh} onChange={(v) => setP("precio_energia_eur_kwh", v)} step={0.01} dec={2} />
          <NumField label="Mantenim. eléctrico" sufix="€/km" value={params.coste_mantenimiento_electrico_eur_km} onChange={(v) => setP("coste_mantenimiento_electrico_eur_km", v)} step={0.01} dec={2} />
          <NumField label="Precio diésel" sufix="€/L" value={params.precio_diesel_eur_l} onChange={(v) => setP("precio_diesel_eur_l", v)} step={0.05} dec={2} />
          <NumField label="Consumo diésel" sufix="L/km" value={params.consumo_diesel_l_km} onChange={(v) => setP("consumo_diesel_l_km", v)} step={0.01} dec={2} />
          <NumField label="Mantenim. diésel" sufix="€/km" value={params.coste_mantenimiento_diesel_eur_km} onChange={(v) => setP("coste_mantenimiento_diesel_eur_km", v)} step={0.01} dec={2} />
        </div>

        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="mt-4 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          data-testid="btn-toggle-advanced"
        >
          {showAdvanced ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          Parámetros avanzados de infraestructura
        </button>
        {showAdvanced && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 pt-3 border-t border-gray-100">
            <NumField label="Cargador depot 50-80 kW" sufix="€" value={params.coste_cargador_depot} onChange={(v) => setP("coste_cargador_depot", v)} step={5000} />
            <NumField label="Cargador oportunidad" sufix="€" value={params.coste_cargador_oportunidad} onChange={(v) => setP("coste_cargador_oportunidad", v)} step={10000} />
            <NumField label="Pantógrafo 450 kW+" sufix="€" value={params.coste_pantografo} onChange={(v) => setP("coste_pantografo", v)} step={10000} />
            <NumField label="Obra civil por plaza" sufix="€" value={params.coste_obra_civil_punto} onChange={(v) => setP("coste_obra_civil_punto", v)} step={5000} />
            <NumField label="Vida útil vehículo" sufix="años" value={params.vida_util_vehiculo} onChange={(v) => setP("vida_util_vehiculo", v)} step={1} />
            <NumField label="Vida útil infraestructura" sufix="años" value={params.vida_util_infraestructura} onChange={(v) => setP("vida_util_infraestructura", v)} step={1} />
          </div>
        )}
      </div>

      {data && (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="crtm-card p-5">
              <h3 className="font-heading font-semibold text-[15px] mb-3">
                CAPEX · Reparto vehículos vs infraestructura
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={breakdownData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={88}
                    paddingAngle={2}
                  >
                    {breakdownData.map((d, i) => (
                      <Cell key={i} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v) => fmtEur(v)}
                  />
                  <Legend
                    iconType="circle"
                    wrapperStyle={{ fontSize: 12, color: "#4B5563" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="crtm-card p-5 lg:col-span-2">
              <h3 className="font-heading font-semibold text-[15px] mb-3">
                CAPEX, OPEX y ahorro por lote (M€)
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={loteChart}>
                  <CartesianGrid stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="lote" tick={{ fontSize: 12, fill: "#4B5563" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} unit=" M€" />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v} M€`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="rect" />
                  <Bar dataKey="CAPEX" fill="#14532D" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="OPEX anual" fill="#991B1B" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="Ahorro anual" fill="#15803D" radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Lote table */}
          <div className="crtm-card">
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-heading font-semibold text-[15px]">
                Resumen por lote
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="crtm-table">
                <thead>
                  <tr>
                    <th>Lote</th>
                    <th>Líneas</th>
                    <th>Vehículos</th>
                    <th>CAPEX vehículos</th>
                    <th>CAPEX infraestr.</th>
                    <th>CAPEX total</th>
                    <th>OPEX anual</th>
                    <th>Ahorro anual</th>
                    <th>Payback</th>
                  </tr>
                </thead>
                <tbody>
                  {data.por_lote.map((l) => (
                    <React.Fragment key={l.lote}>
                      <tr
                        className="clickable"
                        onClick={() =>
                          setExpandedLote(expandedLote === l.lote ? null : l.lote)
                        }
                        data-testid={`inversion-row-${l.lote}`}
                      >
                        <td>
                          <span className="inline-flex items-center gap-1.5">
                            {expandedLote === l.lote ? (
                              <ChevronDown size={13} />
                            ) : (
                              <ChevronRight size={13} />
                            )}
                            <span
                              className="pill"
                              style={{
                                background: LOTE_COLORS[l.lote] + "20",
                                color: LOTE_COLORS[l.lote],
                                borderColor: LOTE_COLORS[l.lote] + "40",
                              }}
                            >
                              {l.lote}
                            </span>
                          </span>
                        </td>
                        <td className="font-mono">{l.n_lineas}</td>
                        <td className="font-mono">{l.n_vehiculos}</td>
                        <td className="font-mono">{fmtEur(l.capex_vehiculos)}</td>
                        <td className="font-mono">{fmtEur(l.capex_infraestructura)}</td>
                        <td className="font-mono font-semibold">{fmtEur(l.capex_total)}</td>
                        <td className="font-mono">{fmtEur(l.opex_anual_total)}</td>
                        <td className="font-mono font-semibold text-green-700">
                          {fmtEur(l.ahorro_anual)}
                        </td>
                        <td className="font-mono">
                          {l.payback_medio ? `${l.payback_medio} años` : "—"}
                        </td>
                      </tr>
                      {expandedLote === l.lote &&
                        data.lineas
                          .filter((li) => li.lote === l.lote)
                          .map((li) => (
                            <tr key={li.codigo_linea} className="bg-gray-50">
                              <td colSpan={2} className="pl-10 text-xs">
                                <div className="font-semibold">
                                  Línea {li.numero_linea}
                                </div>
                                <div className="text-gray-500 truncate max-w-[260px]">
                                  {li.denominacion}
                                </div>
                              </td>
                              <td className="font-mono text-xs">{li.n_vehiculos}</td>
                              <td className="font-mono text-xs">
                                {fmtEur(li.capex_vehiculos)}
                              </td>
                              <td className="font-mono text-xs">
                                {fmtEur(li.capex_infraestructura)}
                                <div className="text-[10px] text-gray-500">
                                  {li.infra_descripcion}
                                </div>
                              </td>
                              <td className="font-mono text-xs font-semibold">
                                {fmtEur(li.capex_total)}
                              </td>
                              <td className="font-mono text-xs">
                                {fmtEur(li.opex_anual_total)}
                              </td>
                              <td className="font-mono text-xs text-green-700">
                                {fmtEur(li.ahorro_anual)}
                              </td>
                              <td className="font-mono text-xs">
                                {li.payback_anios ? `${li.payback_anios} años` : "—"}
                              </td>
                            </tr>
                          ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-900 text-white font-semibold">
                    <td>TOTAL</td>
                    <td className="font-mono">{data.totales.n_lineas}</td>
                    <td className="font-mono">{data.totales.n_vehiculos}</td>
                    <td className="font-mono">{fmtEur(data.totales.capex_vehiculos)}</td>
                    <td className="font-mono">{fmtEur(data.totales.capex_infraestructura)}</td>
                    <td className="font-mono">{fmtEur(data.totales.capex_total)}</td>
                    <td className="font-mono">{fmtEur(data.totales.opex_anual_total)}</td>
                    <td className="font-mono">{fmtEur(data.totales.ahorro_anual)}</td>
                    <td className="font-mono">
                      {data.totales.payback_global
                        ? `${data.totales.payback_global} años`
                        : "—"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Methodology */}
          <div className="crtm-card p-6">
            <h3 className="font-heading font-semibold text-[15px] mb-2 flex items-center gap-2">
              <Clock size={16} /> Hipótesis del cálculo
            </h3>
            <ul className="text-sm text-gray-700 space-y-1.5 leading-relaxed">
              <li>· Vehículos por línea = veh-km anuales ÷ 60.000 (km/año por vehículo).</li>
              <li>· Infraestructura mínima: 1 cargador de cochera por vehículo + obra civil.</li>
              <li>
                · Líneas <strong>Condicionadas</strong>: añaden 1 cargador de oportunidad cada 4 vehículos.
              </li>
              <li>
                · Líneas <strong>A revisar</strong>: añaden 1 pantógrafo (450+ kW) cada 6 vehículos.
              </li>
              <li>· CAPEX incremental para payback: vehículo diésel ≈ 55% del coste del eléctrico equivalente.</li>
              <li>· OPEX eléctrico = consumo anual × precio energía + veh-km × mantenimiento.</li>
              <li>· OPEX diésel = veh-km × (consumo L/km × precio L) + veh-km × mantenimiento diésel.</li>
              <li>· Amortización lineal según vida útil para CAPEX anualizado.</li>
            </ul>
            <p className="text-xs text-gray-400 mt-3">
              Cifras orientativas para planificación. No sustituyen a un análisis financiero detallado con curvas reales de uso, fiscalidad e incentivos.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

const NumField = ({ label, sufix, value, onChange, step = 1, dec = 0 }) => (
  <div>
    <label className="kpi-label" style={{ display: "block", marginBottom: 4 }}>
      {label}
    </label>
    <div className="relative">
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => onChange(parseFloat(e.target.value || 0))}
        className="w-full px-3 py-2 pr-12 border border-gray-200 rounded-md text-sm font-mono focus:outline-none focus:border-gray-900"
        data-testid={`field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
        {sufix}
      </span>
    </div>
  </div>
);

const tooltipStyle = {
  background: "white",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  fontSize: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};
